import { splitProps } from "solid-js";
import { destinationAttrs, type LinkDestination } from "~/common/destination";
import { Link } from "~/inertia";
import { type InertiaLinkProps } from "~/inertia/Link";
import clsx, { type ClassValue, type ClassyComponentProps } from "~/util/html/style";

const TEXT_LINK_CLASSES = "hover:text-brand-link hover:underline";

/** Options shared by every TextLink variant — the future home for any option every variant
 *  supports. */
type TextLinkStyleProps = {
  /** Underline at rest, on top of the usual hover styling. Opt in when the link stands inside a
   *  feed or line of ordinary text where nothing else marks it as clickable; leave it off where
   *  layout, an icon, or a details-grid label already does that job. */
  underline?: boolean;
};

function textLinkClasses(style: TextLinkStyleProps, className?: ClassValue) {
  return clsx(TEXT_LINK_CLASSES, style.underline && "underline", className);
}

type TextLinkProps = InertiaLinkProps & TextLinkStyleProps;

/** Inline text that is a link: inherits the surrounding color, font and size, and shows itself on
 *  hover. Contrast the standalone, always-blue `ButtonLink variant="link"` with button layout. */
export function TextLink(props: TextLinkProps) {
  const [local, rest] = splitProps(props, ["underline", "class"]);

  return <Link {...rest} class={textLinkClasses(local, local.class)} />;
}

type TextLinkAProps = ClassyComponentProps<"a"> &
  TextLinkStyleProps & {
    destination?: LinkDestination;
  };

/** The same, over a plain anchor that leaves the Inertia app. */
export function TextLinkA(props: TextLinkAProps) {
  const [local, rest] = splitProps(props, ["destination", "underline", "class"]);

  return (
    <a
      {...rest}
      {...destinationAttrs(local.destination)}
      // These anchors leave the Inertia app (new tabs, external sites, Hotwire pages), so
      // Turbo must never intercept the navigation.
      data-turbo="false"
      class={textLinkClasses(local, local.class)}
    />
  );
}
